/**
 * Which columns the OVS split editor shows, and which age-group rows a school
 * value replaces — pure, so the storage rules are unit-testable without React.
 *
 * ## The model (Sheila, 2026-11-04)
 *
 * *"OVS should be By Org, with location level overrides… and location level
 * can have age level overrides. But it should store as 1 value if it's an
 * override."*
 *
 *   1. The DISTRICT stores one value per meal part (the ORGANIZATION tier).
 *   2. A SCHOOL may override it with ONE value per meal part — a single
 *      campus-wide (null-tuple) LOCATION row, never one row per age group.
 *   3. Only when a school genuinely serves an age group differently does that
 *      age group get its own row, and the editor makes that an explicit,
 *      opt-in exception.
 *
 * Before this, a district that orders by age group got ONE COLUMN PER AGE
 * GROUP and no school column at all (`buildOrderColumns`), so every school
 * edit was stored as a row per age group. New Hyde Park ended up with fruit
 * and vegetable stored twice (K-8 and Preschool) on top of a campus row that
 * no student resolved to, and milk still at that stale campus value.
 *
 * ## Why a school edit removes that part's age-group rows
 *
 * The resolver ranks an age group's own row ABOVE the school row
 * (`rate-cascade.ts`, exact tuple beats wide). Writing a new school value
 * while leaving the age-group rows in place would change nothing for any
 * student in those groups — the save would look successful and ship the old
 * number. So a changed school value is the one value for that part, and the
 * age-group rows it would otherwise sit beneath are archived with it. An
 * exception typed in the SAME save is kept: that is the user saying "this
 * group differs".
 */

import type { RateGroupDimension } from '@/api/location-settings'
import type { Mealtime } from '@/lib/mealtime'

import { type OrderColumn, WHOLE_SCHOOL_COLUMN_ID } from './columns'
import { rateRowColumnKey } from './milk'

/** The editor's single campus-wide column. Edits the null-tuple rows. */
export function schoolSplitColumn(label = 'This school'): OrderColumn {
  return {
    columnId: WHOLE_SCHOOL_COLUMN_ID,
    audienceId: WHOLE_SCHOOL_COLUMN_ID,
    label,
    sublabel: null,
    studentCount: null,
    gradeRangeId: null,
    gradeRangeCode: null,
    groupDimension: null,
    groupValue: null,
    sortOrder: 0,
    serviceStyle: 'OVS',
  }
}

/** True when the campus orders by a group axis, so age-group exceptions exist. */
export function hasGroupColumns(orderColumns: readonly OrderColumn[]): boolean {
  return orderColumns.some((column) => column.columnId !== WHOLE_SCHOOL_COLUMN_ID)
}

/**
 * The editor's columns: the school column first, then the order columns only
 * when the age-group exceptions are open. A campus that already orders as one
 * whole school keeps exactly its one column.
 */
export function splitEditorColumns(
  orderColumns: readonly OrderColumn[],
  options: { showGroups: boolean; schoolLabel?: string },
): OrderColumn[] {
  if (!hasGroupColumns(orderColumns)) {
    return [...orderColumns]
  }
  const groups = orderColumns.filter((column) => column.columnId !== WHOLE_SCHOOL_COLUMN_ID)
  return [schoolSplitColumn(options.schoolLabel), ...(options.showGroups ? groups : [])]
}

/** A stored row as these helpers need it. */
interface GroupScopedRow {
  groupDimension: RateGroupDimension | null
  groupValue: string | null
  mealtime: Mealtime | null
}

/** One age-group row's write scope. */
export interface GroupRowScope {
  groupDimension: RateGroupDimension
  groupValue: string
}

function groupScopeOf(row: GroupScopedRow): GroupRowScope | null {
  return row.groupDimension && row.groupValue !== null
    ? { groupDimension: row.groupDimension, groupValue: row.groupValue }
    : null
}

/**
 * The age-group rate rows a school value for (`componentKind`, `mealtime`)
 * would sit beneath. Only this mealtime's own rows: a legacy all-mealtimes
 * group row also applies to every other mealtime, so it is not this edit's to
 * remove.
 */
export function groupRateRowsFor<T extends GroupScopedRow & { componentKind: string }>(
  rows: readonly T[],
  componentKind: string,
  mealtime: Mealtime,
): GroupRowScope[] {
  return rows
    .filter((row) => row.componentKind === componentKind && row.mealtime === mealtime)
    .map(groupScopeOf)
    .filter((scope): scope is GroupRowScope => scope !== null)
}

/** The distinct age-group milk-share sets stored for `mealtime`. */
export function groupShareSetsFor(rows: readonly GroupScopedRow[], mealtime: Mealtime): GroupRowScope[] {
  const byKey = new Map<string, GroupRowScope>()
  for (const row of rows) {
    const scope = row.mealtime === mealtime ? groupScopeOf(row) : null
    if (scope) {
      byKey.set(`${scope.groupDimension}::${scope.groupValue}`, scope)
    }
  }
  return [...byKey.values()]
}

/** The column key `rateRowColumnKey` gives a group scope — the editor's cell key half. */
export function groupScopeColumnKey(scope: GroupRowScope): string {
  return rateRowColumnKey(scope) ?? 'default'
}

/**
 * Drop the scopes the user is editing as exceptions in this same save, so the
 * school write does not archive the number they just typed for that group.
 */
export function supersededGroupScopes(
  scopes: readonly GroupRowScope[],
  stagedColumnKeys: ReadonlySet<string>,
): GroupRowScope[] {
  return scopes.filter((scope) => !stagedColumnKeys.has(groupScopeColumnKey(scope)))
}

/** The meal parts (and milk) that carry age-group rows at `mealtime`, for the notice. */
export function groupOverrideSummary(
  rates: ReadonlyArray<GroupScopedRow & { componentKind: string }>,
  shares: readonly GroupScopedRow[],
  mealtime: Mealtime,
): { componentKinds: string[]; milkShares: boolean; count: number } {
  const kinds = new Set<string>()
  let count = 0
  for (const row of rates) {
    if (row.mealtime === mealtime && groupScopeOf(row)) {
      kinds.add(row.componentKind)
      count += 1
    }
  }
  const milkSets = groupShareSetsFor(shares, mealtime)
  return { componentKinds: [...kinds], milkShares: milkSets.length > 0, count: count + milkSets.length }
}
